/**
 * Task #1305 — AI Routing v2 — Sezione catalog + recommender per ogni service_key.
 *
 * Per ogni service_key di `platform_model_config` mostra:
 *   - top-3 raccomandati (badge TOP, score, motivation, breakdown 4-dim)
 *   - "all others" collassabile
 *   - badge: deprecated/preview/new/metadata_complete/modality
 *   - tooltip breakdown (quality·wq + cost·wc + latency·wl + feature_match·wf)
 *
 * Banner sticky se catalog stale > 60g. Read-only sui pesi (PUT separato).
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Loader2, RefreshCw, AlertTriangle, Info, Sparkles, Trash2, Star, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient, type ApiResponse } from '@/lib/api';
import ModelCatalogByProvider from './ModelCatalogByProvider';
import {
  CatalogStalenessSchema, RecommendationResponseSchema, DiscoveryResponseSchema,
  type CatalogStaleness, type ScoredModel, type RecommendationResponse, type DiscoveryResponse,
} from '@easyflow/shared';

const PROVIDER_COLORS: Record<string, string> = {
  openai: 'bg-sky-100 text-sky-700 border-sky-200',
  claude: 'bg-purple-100 text-purple-700 border-purple-200',
  google: 'bg-blue-100 text-blue-700 border-blue-200',
};

// SERVICE_KEYS allineato a platform_model_config DEFAULT_CONFIGS.
const SERVICE_KEYS: Array<{ key: string; label: string }> = [
  { key: 'embedding',                 label: 'Embedder (vettori KB)' },
  { key: 'rag_synthesis',             label: 'RAG Synthesis' },
  { key: 'rag_decomposition',         label: 'RAG Decomposer' },
  { key: 'rag_rerank',                label: 'RAG Reranker' },
  { key: 'rag_eval',                  label: 'RAG Eval (offline)' },
  { key: 'rag_query_expansion_model', label: 'RAG Query Expansion / HyDE' },
  { key: 'rag_query_expansion',       label: 'RAG Query Expansion (config)' },
  { key: 'rag_topk_strategy',         label: 'RAG Adaptive Top-K (config)' },
  { key: 'rag_quality_features',      label: 'RAG Quality Features (config)' },
  { key: 'tts_ivr',                   label: 'TTS IVR' },
  { key: 'stt_voicemail',             label: 'STT Voicemail' },
  { key: 'stt_video',                 label: 'STT Video' },
  { key: 'stt_realtime',              label: 'STT Realtime' },
  { key: 'workflow_builder',          label: 'Workflow Builder' },
  { key: 'file_analysis',             label: 'File Analysis' },
  { key: 'file_summary',              label: 'File Summary' },
  { key: 'landing_fallback',          label: 'Landing Fallback' },
  { key: 'landing_vision',            label: 'Landing Vision' },
  { key: 'ocr',                       label: 'OCR' },
  { key: 'document_parsing',          label: 'Parser POS' },
];

function ModelRow({ m, weights, isTop, idx, serviceKey }: {
  m: ScoredModel; weights: RecommendationResponse['weights']; isTop: boolean; idx: number; serviceKey: string;
}): JSX.Element {
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 p-2 rounded border ${isTop && idx === 0 ? 'border-violet-200 bg-violet-50/40 dark:bg-violet-950/20 dark:border-violet-900' : 'border-gray-200 dark:border-gray-800'}`}
      data-testid={`reco-row-${serviceKey}-${isTop ? 'top' : 'other'}-${idx}`}
    >
      <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
        {isTop && idx === 0 && <Badge className="text-[9px] bg-violet-100 text-violet-700 border-violet-200"><Star className="h-2.5 w-2.5 mr-0.5" />TOP</Badge>}
        <Badge className={`text-[9px] ${PROVIDER_COLORS[m.provider] ?? ''}`}>{m.provider}</Badge>
        <span className="text-xs font-mono truncate" title={m.display_name ?? undefined}>{m.model_id}</span>
        {m.is_new && <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200">NEW</Badge>}
        {m.is_deprecated && <Badge className="text-[9px] bg-red-100 text-red-700 border-red-200">deprecated</Badge>}
        {m.catalog.preview && <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200">preview</Badge>}
        {!m.metadata_complete && (
          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <Badge className="text-[9px] bg-gray-100 text-gray-600 border-gray-200 cursor-help">meta?</Badge>
              </TooltipTrigger>
              <TooltipContent side="top"><p className="text-[11px]">Metadata incompleta (pricing/context_window/modality)</p></TooltipContent>
            </UITooltip>
          </TooltipProvider>
        )}
        {m.catalog.modality.length > 1 && (
          <Badge variant="outline" className="text-[9px]">{m.catalog.modality.join('+')}</Badge>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[10px] text-gray-500 italic max-w-[12rem] sm:max-w-[260px] truncate" title={m.motivation}>{m.motivation}</span>
        <span className="text-xs font-semibold tabular-nums">{m.score.toFixed(3)}</span>
        <TooltipProvider>
          <UITooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs">
              <div className="text-[11px] space-y-1">
                <div className="font-semibold">Scoring breakdown (4-dim)</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono">
                  <span>quality</span>       <span className="text-right">{m.breakdown.quality.toFixed(3)} × {Number(weights.weight_quality).toFixed(2)}</span>
                  <span>cost</span>          <span className="text-right">{m.breakdown.cost.toFixed(3)} × {Number(weights.weight_cost).toFixed(2)}</span>
                  <span>latency</span>       <span className="text-right">{m.breakdown.latency.toFixed(3)} × {Number(weights.weight_latency).toFixed(2)}</span>
                  <span>feature_match</span> <span className="text-right">{m.breakdown.feature_match.toFixed(3)} × {Number(weights.weight_feature_match).toFixed(2)}</span>
                </div>
                {m.observed && (
                  <div className="pt-1 border-t border-gray-300 dark:border-gray-700 text-gray-500">
                    p50 {m.observed.p50_latency_ms ?? '—'}ms · p95 {m.observed.p95_latency_ms ?? '—'}ms · n={m.observed.sample_count}
                    {m.observed.used_for_scoring ? ' (osservato)' : ' (prior, n<100)'}
                  </div>
                )}
              </div>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}

export default function AiModelsCatalogSection(): JSX.Element {
  const { toast } = useToast();
  const { user } = useAuth();
  // /admin/models/discover e /cache/invalidate richiedono il master admin.
  const canManagePlatformCatalog = user?.role === 'super_admin';
  const [loading, setLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [recos, setRecos] = useState<Record<string, RecommendationResponse | { error: string }>>({});
  const [staleness, setStaleness] = useState<CatalogStaleness | null>(null);
  // Vista per provider (catalogo) di default; i consigli per funzione si
  // caricano solo quando la scheda viene aperta.
  const [view, setView] = useState<'provider' | 'function'>('provider');

  const loadAll = useCallback(async (embedderProvider?: 'openai' | 'gemini' | null) => {
    setLoading(true);
    try {
      const catRes: ApiResponse<{ staleness: CatalogStaleness }> = await apiClient.get('/admin/models/catalog');
      setStaleness(catRes.data?.staleness ? CatalogStalenessSchema.parse(catRes.data.staleness) : null);

      const epQuery = embedderProvider ? `&embedder_provider=${embedderProvider}` : '';
      const promises = SERVICE_KEYS.map(async ({ key }) => {
        try {
          const r: ApiResponse<RecommendationResponse> = await apiClient.get(
            `/admin/models/recommend?service_key=${encodeURIComponent(key)}${epQuery}`,
          );
          return [key, r.data ? RecommendationResponseSchema.parse(r.data) : { error: 'no data' }] as const;
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : String(e);
          return [key, { error: msg }] as const;
        }
      });
      const results = await Promise.all(promises);
      const map: Record<string, RecommendationResponse | { error: string }> = {};
      for (const [k, v] of results) map[k] = v;
      setRecos(map);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Errore caricamento catalog', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const [functionViewLoaded, setFunctionViewLoaded] = useState(false);
  useEffect(() => {
    if (view !== 'function' || functionViewLoaded) return;
    setFunctionViewLoaded(true);
    void loadAll();
  }, [view, functionViewLoaded, loadAll]);

  const triggerDiscovery = useCallback(async () => {
    setDiscovering(true);
    try {
      const r: ApiResponse<DiscoveryResponse> = await apiClient.post('/admin/models/discover', {});
      const results = r.data ? DiscoveryResponseSchema.parse(r.data).results : [];
      const okCount = results.filter((x) => x.ok).reduce((s, x) => s + x.count, 0);
      const stale = results.filter((x) => x.stale).map((x) => `${x.provider} (cache ${x.last_refresh_at ?? '?'})`).join(' · ');
      const failed = results.filter((x) => !x.ok).map((x) => `${x.provider}: ${x.error}`).join(' · ');
      toast({
        title: 'Discovery completata',
        description: [`${okCount} modelli aggiornati`, stale && `Stale: ${stale}`, failed && `Errori: ${failed}`].filter(Boolean).join(' · '),
      });
      await loadAll();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Errore discovery', description: msg, variant: 'destructive' });
    } finally {
      setDiscovering(false);
    }
  }, [loadAll, toast]);

  const invalidateCache = useCallback(async () => {
    try {
      await apiClient.post('/admin/models/cache/invalidate', {});
      await loadAll();
      toast({ title: 'Cache invalidata', description: 'Recommendations ricaricate.' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Errore invalidazione', description: msg, variant: 'destructive' });
    }
  }, [loadAll, toast]);

  const stale = useMemo(() => (staleness?.stale_days ?? 0) > 60, [staleness]);

  return (
    <div className="space-y-3" data-testid="ai-models-catalog-section">
      {stale && (
        <div
          data-testid="ai-models-stale-banner"
          className="sticky top-0 z-20 px-3 py-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 text-amber-900 dark:text-amber-100 text-xs flex items-center gap-2"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            Catalog modelli stale: oldest <strong>{staleness?.stale_days}g</strong> fa. Esegui Discovery per refresh.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="inline-flex rounded-lg border bg-muted/40 p-0.5" role="tablist" aria-label="Vista del catalogo">
            {([['provider', 'Per provider'], ['function', 'Consigliati per funzione']] as const).map(([id, text]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={view === id}
                onClick={() => setView(id)}
                data-testid={`catalog-view-${id}`}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  view === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {text}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            {view === 'provider'
              ? 'Tutti i modelli disponibili, con capacità, input supportati, contesto e prezzo. Si aggiorna da solo ogni 6 ore.'
              : 'I 3 modelli migliori per ogni funzione (qualità, costo, latenza, funzioni supportate) e tutti gli altri compatibili.'}
          </p>
        </div>
        {/* Cache e discovery sono operazioni di piattaforma (master): nascoste agli admin tenant. */}
        {canManagePlatformCatalog && (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={invalidateCache} disabled={loading || discovering}>
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Cache
          </Button>
          <Button variant="outline" size="sm" onClick={triggerDiscovery} disabled={discovering || loading}>
            {discovering ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            Discovery
          </Button>
        </div>
        )}
      </div>

      {view === 'provider' && <ModelCatalogByProvider />}

      {view === 'function' && loading && (
        <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="h-3 w-3 animate-spin" /> Caricamento dei consigli…</div>
      )}

      {view === 'function' && (
      <div className="grid gap-2">
        {SERVICE_KEYS.map(({ key, label }) => {
          const r = recos[key];
          if (!r) return null;
          if ('error' in r) {
            return (
              <Card key={key} className="border-red-200 dark:border-red-900">
                <CardContent className="p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{label} <code className="text-[10px] text-gray-500">({key})</code></span>
                    <span className="text-red-600 dark:text-red-400">errore: {r.error}</span>
                  </div>
                </CardContent>
              </Card>
            );
          }
          return (
            <Card key={key} data-testid={`reco-card-${key}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
                    <code className="text-[10px] text-gray-500">{key} · {r.capability}</code>
                  </div>
                  <Badge variant="secondary" className="text-[10px]">{r.top.length} top · {r.others.length} altri</Badge>
                </div>
                <div className="grid gap-1.5">
                  {r.top.map((m, idx) => (
                    <ModelRow key={`${m.provider}::${m.model_id}`} m={m} weights={r.weights} isTop={true} idx={idx} serviceKey={key} />
                  ))}
                </div>
                {r.others.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-gray-500 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1 select-none list-none">
                      <ChevronDown className="h-3 w-3" />
                      Mostra altri {r.others.length} modelli
                    </summary>
                    <div className="grid gap-1.5 mt-1.5">
                      {r.others.map((m, idx) => (
                        <ModelRow key={`${m.provider}::${m.model_id}`} m={m} weights={r.weights} isTop={false} idx={idx} serviceKey={key} />
                      ))}
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}
    </div>
  );
}
